import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { Heart, Search } from 'lucide-react'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from 'tailwind-v4/components/input-group'
import loyaltyApi from '@/state/loyalty/loyalty.slice'

function SchemeInitial({ name }: { name: string }) {
  const initial = (name?.[0] ?? '?').toUpperCase()
  return (
    <span
      aria-hidden
      className="flex size-5 shrink-0 items-center justify-center rounded-sm bg-accent text-[10px] font-semibold text-accent-foreground"
    >
      {initial}
    </span>
  )
}

/**
 * "Pick a scheme to continue" gate — the loyalty twin of `VenuePickerGate`.
 * Every loyalty service (Enrolled Guests, Rewards, Vouchers, …) only exists
 * under a scheme, so landing on one without a scheme selected shows this
 * rather than bouncing the user back to the schemes list and making them work
 * out where they were headed.
 */
export function SchemePickerGate({
  label,
  onPick,
}: {
  label: string
  onPick: (schemeId: string) => void
}) {
  const { org_id } = useParams<{ org_id: string }>()
  const [search, setSearch] = useState('')

  const { data: schemes = [] } = loyaltyApi.useGetLoyaltySchemesQuery(
    { orgId: org_id ?? '' },
    { skip: !org_id }
  )

  const filtered = useMemo(() => {
    if (!search) return schemes as any[]
    const q = search.toLowerCase()
    return (schemes as any[]).filter((s) =>
      (s?.name ?? '').toLowerCase().includes(q)
    )
  }, [schemes, search])

  return (
    <div className="flex flex-1 items-start justify-center px-4 py-16">
      <div className="flex w-full max-w-md flex-col gap-6 text-center">
        <div className="mx-auto flex size-10 items-center justify-center rounded-xl border border-border text-muted-foreground">
          <Heart className="size-5" />
        </div>
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-semibold text-foreground">
            Continue to {label}
          </h2>
          <p className="text-sm text-muted-foreground">
            Choose a scheme to continue
          </p>
        </div>
        <div className="flex flex-col gap-2 text-left">
          <InputGroup className="h-10">
            <InputGroupAddon>
              <Search className="size-4" />
            </InputGroupAddon>
            <InputGroupInput
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
              }}
              placeholder="Find scheme…"
              aria-label="Find scheme"
            />
          </InputGroup>
          {filtered.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No scheme found.
            </p>
          ) : (
            <ul className="flex flex-col gap-1">
              {filtered.map((scheme: any) => {
                const id = scheme?.id as string
                const name = (scheme?.name as string) ?? 'Unnamed Scheme'
                return (
                  <li key={id}>
                    <button
                      type="button"
                      onClick={() => {
                        onPick(id)
                      }}
                      className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-foreground outline-none transition-colors hover:bg-accent focus-visible:bg-accent"
                    >
                      <SchemeInitial name={name} />
                      <span className="truncate">{name}</span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Route wrapper around `<SchemePickerGate />`, mirroring `VenueGatePage`.
 * Mounted at the scheme-less path for each loyalty service (e.g.
 * `/orgId/loyalty/customers`); on pick it forwards to the same service under
 * the chosen scheme. Keeps the venue scope when there's a serial in the URL,
 * so the gate works from both `/orgId/loyalty/...` and
 * `/orgId/venues/:serial/loyalty/...`.
 */
export function SchemeGatePage({
  label,
  service,
}: {
  label: string
  /** Service segment to land on. Omit for the scheme's overview (its root). */
  service?: string
}) {
  const { org_id, serial } = useParams<{ org_id: string; serial: string }>()
  const navigate = useNavigate()

  function onPick(schemeId: string) {
    const base = serial
      ? `/${org_id}/venues/${serial}/loyalty/${schemeId}`
      : `/${org_id}/loyalty/${schemeId}`
    navigate(service ? `${base}/${service}` : base, {
      viewTransition: true,
      replace: true,
    })
  }

  return (
    <main className="flex flex-1 flex-col">
      <SchemePickerGate label={label} onPick={onPick} />
    </main>
  )
}
